export type LumiEvent = {
  timestamp: number;
  lessonId: string;
  widgetId: string;
  type: string;
  payload: Record<string, unknown>;
};

class LumiObserver {
  private events: LumiEvent[] = [];
  private subscribers: ((e: LumiEvent) => void)[] = [];
  private readonly maxEvents = 200;

  emit(e: Omit<LumiEvent, 'timestamp'>) {
    const event: LumiEvent = { ...e, timestamp: Date.now() };
    if (!Array.isArray(this.events)) this.events = [];
    this.events.push(event);
    if (this.events.length > this.maxEvents) this.events.shift();
    this.persist();
    // eslint-disable-next-line no-console
    console.log('[Lumi]', e.type, '| lessonId:', e.lessonId, '| widgetId:', e.widgetId, '| total events:', this.events.length);
    this.subscribers.forEach(fn => fn(event));
  }

  subscribe(fn: (e: LumiEvent) => void) {
    this.subscribers.push(fn);
    return () => { this.subscribers = this.subscribers.filter(f => f !== fn); };
  }

  getRecent(lessonId: string, n = 50): LumiEvent[] {
    return this.events.filter(e => e.lessonId === lessonId).slice(-n);
  }

  getAllForLesson(lessonId: string): LumiEvent[] {
    return this.events.filter(e => e.lessonId === lessonId);
  }

  resetWidget(lessonId: string, widgetId: string) {
    this.emit({
      lessonId,
      widgetId,
      type: 'widget_reset',
      payload: { priorEventCount: this.events.filter(e => e.lessonId === lessonId && e.widgetId === widgetId).length }
    });
  }

  private persist() {
    try {
      localStorage.setItem('lumio_lumi_observer_events', JSON.stringify(this.events.slice(-this.maxEvents)));
    } catch { /* storage full — degrade silently */ }
  }

  hydrate() {
    try {
      const stored = localStorage.getItem('lumio_lumi_observer_events');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.events = Array.isArray(parsed) ? parsed as LumiEvent[] : [];
      }
    } catch { this.events = []; }
  }
}

// ── Global singleton ───────────────────────────────────────────────
// CRA code-splitting causes observer.ts to be bundled into every lazy
// chunk that imports it, creating multiple LumiObserver instances.
// Widgets emit to their chunk's instance; HoveringLumi subscribes to
// a different instance — they never communicate.
//
// Fix: store the singleton on window so all chunks share ONE object,
// regardless of how many times this module is evaluated.
declare global {
  interface Window { __lumiObserver__?: LumiObserver; }
}

if (!window.__lumiObserver__) {
  const obs = new LumiObserver();
  obs.hydrate();
  window.__lumiObserver__ = obs;
}

export const lumiObserver: LumiObserver = window.__lumiObserver__!;
